
import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { usePublicBlog } from "@/hooks/useCMSBlog";
import { getLocalized } from "@/utils/localization";
import i18n from "@/i18n";

// ─── Article Content ──────────────────────────────────────────────────────────

// ─── Article Content ──────────────────────────────────────────────────────────

const articleSections: Record<string, { ar: { heading: string; body: string; icon?: string }[], en: { heading: string; body: string; icon?: string }[] }> = {
  "precision-medicine-future": {
    ar: [
      {
        icon: "ri-focus-3-line",
        heading: "لماذا لم تعد الحلول العامة كافية؟",
        body: "الطب التقليدي يعامل المرضى كفئات متجانسة — بروتوكول واحد لكل تشخيص. لكن الواقع البيولوجي مختلف تماماً: كل شخص يحمل جينوماً فريداً، وميكروبيوماً مختلفاً، وتاريخاً صحياً لا يشبه غيره. الطب الدقيق يبدأ من هذه الحقيقة.",
      },
      {
        icon: "ri-stack-line",
        heading: "التقييم متعدد الطبقات: ما وراء الفحص الروتيني",
        body: "في ذا مديكال أفينيو، التقييم لا يبدأ بالشكوى وينتهي بالوصفة. نبدأ بالاستماع العميق (30–45 دقيقة)، ثم نفكك الحالة عبر طبقات سريرية ومخبرية وجينية، ثم نربط بين الحاضر الصحي والمخاطر المستقبلية. الجلسة الواحدة قد تمتد 90–120 دقيقة.",
      },
      {
        icon: "ri-dna-line",
        heading: "DNA Risk Score: قراءة المستقبل الصحي",
        body: "تحليل الـ DNA لا يُستخدم لدينا للاستعراض التقني، بل لفهم الاستعدادات الجينية الحقيقية: هل هناك خطر مرتفع لأمراض القلب؟ السكري؟ الأورام؟ هذه المعلومات تُغير خطة الرعاية من التفاعل مع المرض إلى الاستباق قبل حدوثه.",
      },
      {
        icon: "ri-global-line",
        heading: "الامتداد الدولي: عندما تحتاج الحالة منظوراً أوسع",
        body: "بعض الحالات تتجاوز الإطار المحلي. شبكة علاقاتنا مع مراكز متخصصة في الولايات المتحدة وكوريا الجنوبية تتيح Second Opinion منظماً للحالات المعقدة — ليس كخدمة شكلية، بل كأداة لاتخاذ قرار أكثر اطمئناناً.",
      },
      {
        icon: "ri-user-heart-line",
        heading: "الخطة الشخصية: لا بروتوكولات عامة",
        body: "نهاية كل تقييم ليست وصفة طبية، بل خطة مبنية على بيولوجيتك الخاصة: ماذا تأكل، كيف تتحرك، ما الفحوصات الدورية التي تحتاجها، وكيف تتابع مؤشراتك الصحية. الخطة تتطور مع الوقت بناءً على استجابتك.",
      },
    ],
    en: [
      {
        icon: "ri-focus-3-line",
        heading: "Why General Solutions Are No Longer Enough",
        body: "Traditional medicine treats patients as homogeneous groups — one protocol for each diagnosis. But biological reality is completely different: everyone carries a unique genome, a different microbiome, and a health history like no other. Precision medicine starts with this truth.",
      },
      {
        icon: "ri-stack-line",
        heading: "Multi-layer Assessment: Beyond Routine Screenings",
        body: "At The Medical Avenue, assessment does not start with a complaint and end with a prescription. We begin with deep listening (30–45 minutes), then dissect the case across clinical, laboratory, and genetic layers, and connect present health status with future risks. A single session can last 90–120 minutes.",
      },
      {
        icon: "ri-dna-line",
        heading: "DNA Risk Score: Reading Your Health Future",
        body: "DNA analysis is not used for technical showmanship, but to understand actual genetic predispositions: is there an elevated risk of heart disease? Diabetes? Tumors? This information changes the care plan from reacting to disease to preempting it before it occurs.",
      },
      {
        icon: "ri-global-line",
        heading: "International Extension: When a Broader Perspective is Needed",
        body: "Some cases go beyond local boundaries. Our network of relationships with specialized centers in the USA and South Korea allows for a structured Second Opinion in complex cases — not as a token service, but as a tool for making more reassured decisions.",
      },
      {
        icon: "ri-user-heart-line",
        heading: "Personalized Plan: No General Protocols",
        body: "The end of each assessment is not a prescription, but a plan built on your own biology: what to eat, how to move, what routine checkups you need, and how to track your health indicators. The plan evolves over time based on your response.",
      },
    ]
  },
  "masld-metabolic": {
    ar: [
      {
        icon: "ri-scales-3-line",
        heading: "MASLD: أكثر من مجرد كبد دهني",
        body: "MASLD (Metabolic dysfunction-Associated Steatotic Liver Disease) ليس مجرد تراكم دهون في الكبد. هو مرآة لخلل أيضي عميق يشمل مقاومة الأنسولين، الالتهاب المزمن، واضطراب الميكروبيوم. علاجه يبدأ بفهم هذا الخلل، لا بتقليل الوزن فحسب.",
      },
      {
        icon: "ri-heart-pulse-line",
        heading: "الصلة بين الكبد والقلب والدماغ",
        body: "الخلل الأيضي لا يبقى في الكبد. يرتبط MASLD بزيادة خطر أمراض القلب والأوعية الدموية، مقاومة الأنسولين، وحتى التدهور المعرفي. التقييم الشامل يجب أن يشمل هذه الأبعاد كلها.",
      },
      {
        icon: "ri-test-tube-line",
        heading: "الفحوصات التي تكشف الحقيقة",
        body: "تشخيص MASLD لا يكتفي بالسونار. نحتاج: تقييم مقاومة الأنسولين (HOMA-IR)، مؤشرات الالتهاب (hsCRP)، تحليل الدهون المتقدم (LDL particle size)، وأحياناً تحليل الميكروبيوم. هذه الفحوصات تكشف ما يخفيه الفحص الروتيني.",
      },
      {
        icon: "ri-leaf-line",
        heading: "التدخل الشخصي: ما يناسبك لا يناسب غيرك",
        body: "لا يوجد نظام غذائي واحد لكل مريض MASLD. بعض المرضى يستجيبون للصيام المتقطع، وآخرون للنظام المتوسطي، وثالثون يحتاجون تدخلاً دوائياً. الخطة تُبنى على استجابتك البيولوجية الفعلية.",
      },
      {
        icon: "ri-line-chart-line",
        heading: "المتابعة: المعركة الحقيقية",
        body: "MASLD حالة مزمنة تحتاج متابعة لصيقة. نراقب المؤشرات كل 3 أشهر، نعدّل الخطة بناءً على الاستجابة، ونستخدم التطبيق لمراقبة المؤشرات اليومية. الهدف ليس تحسين الفحوصات فقط، بل تحسين جودة الحياة الفعلية.",
      },
    ],
    en: [
      {
        icon: "ri-scales-3-line",
        heading: "MASLD: More Than Just a Fatty Liver",
        body: "MASLD (Metabolic dysfunction-Associated Steatotic Liver Disease) is not just fat accumulation in the liver. It is a mirror of a deep metabolic defect that includes insulin resistance, chronic inflammation, and microbiome disruption. Treating it begins with understanding this defect, not just losing weight.",
      },
      {
        icon: "ri-heart-pulse-line",
        heading: "The Connection Between Liver, Heart, and Brain",
        body: "Metabolic dysfunction does not stay in the liver. MASLD is associated with an increased risk of cardiovascular disease, insulin resistance, and even cognitive decline. A comprehensive assessment must include all of these dimensions.",
      },
      {
        icon: "ri-test-tube-line",
        heading: "Tests That Reveal the Truth",
        body: "Diagnosing MASLD is not limited to ultrasound. We need: insulin resistance assessment (HOMA-IR), inflammation indicators (hsCRP), advanced lipid analysis (LDL particle size), and sometimes microbiome analysis. These tests reveal what routine checks hide.",
      },
      {
        icon: "ri-leaf-line",
        heading: "Personalized Intervention: What Suits You May Not Suit Others",
        body: "There is no single diet for every MASLD patient. Some patients respond to intermittent fasting, others to the Mediterranean diet, and others need pharmacological intervention. The plan is built on your actual biological response.",
      },
      {
        icon: "ri-line-chart-line",
        heading: "Follow-up: The Real Battle",
        body: "MASLD is a chronic condition that needs close follow-up. We monitor indicators every 3 months, adjust the plan based on response, and use the app to monitor daily metrics. The goal is not just to improve test results, but to improve actual quality of life.",
      },
    ]
  },
  "second-opinion-value": {
    ar: [
      {
        icon: "ri-question-answer-line",
        heading: "متى يكون الرأي الثاني ضرورة؟",
        body: "ليس كل حالة تحتاج Second Opinion، لكن بعض الحالات لا تحتمل القرار بدونه: التشخيصات النادرة، الجراحات الكبرى، الأورام، الحالات التي لم تستجب للعلاج المعتاد، أو عندما يشعر المريض بعدم الاطمئنان لقرار طبي مهم.",
      },
      {
        icon: "ri-global-line",
        heading: "Second Opinion الدولي: كيف يعمل؟",
        body: "في ذا مديكال أفينيو، Second Opinion الدولي ليس مجرد إرسال ملف. نُعدّ الحالة بشكل احترافي، نختار المركز المناسب (USA أو Korea أو غيرها)، نُترجم الوثائق، ونُلخص الحالة بلغة علمية دقيقة. ثم نُترجم الرأي الدولي ونشرحه للمريض بوضوح.",
      },
      {
        icon: "ri-shield-check-line",
        heading: "الرأي الثاني لا يعني عدم الثقة",
        body: "كثير من المرضى يترددون في طلب رأي ثانٍ خشية إزعاج طبيبهم. الحقيقة أن الأطباء الواثقين من قراراتهم يرحبون بالرأي الثاني — لأنه يُعزز القرار أو يُصحح مساره. في كلتا الحالتين، المريض يكسب.",
      },
      {
        icon: "ri-file-text-line",
        heading: "ما الذي يتغير بعد Second Opinion؟",
        body: "في دراسات دولية، Second Opinion غيّر التشخيص أو خطة العلاج في 21–62% من الحالات المعقدة. هذا لا يعني أن الطبيب الأول أخطأ — بل يعني أن الطب علم احتمالات، والمزيد من الخبرات يُقلل هامش الخطأ.",
      },
    ],
    en: [
      {
        icon: "ri-question-answer-line",
        heading: "When is a Second Opinion a Necessity?",
        body: "Not every case needs a Second Opinion, but some cases cannot afford a decision without it: rare diagnoses, major surgeries, tumors, cases that did not respond to standard treatment, or when a patient feels unsure about an important medical decision.",
      },
      {
        icon: "ri-global-line",
        heading: "International Second Opinion: How Does It Work?",
        body: "At The Medical Avenue, an international Second Opinion is not just sending a file. We professionally prepare the case, choose the right center (USA, Korea, etc.), translate documents, and summarize the case in precise scientific language. Then we translate the international opinion and explain it clearly to the patient.",
      },
      {
        icon: "ri-shield-check-line",
        heading: "Second Opinion Does Not Mean Lack of Trust",
        body: "Many patients hesitate to ask for a second opinion for fear of upsetting their doctor. The truth is that doctors who are confident in their decisions welcome a second opinion — because it reinforces the decision or corrects its course. In both cases, the patient wins.",
      },
      {
        icon: "ri-file-text-line",
        heading: "What Changes After a Second Opinion?",
        body: "In international studies, a Second Opinion changed the diagnosis or treatment plan in 21–62% of complex cases. This does not mean the first doctor made a mistake — it means medicine is a science of probabilities, and more expertise reduces the margin of error.",
      },
    ]
  },
  "dna-risk-score": {
    ar: [
      {
        icon: "ri-dna-line",
        heading: "ما هو DNA Risk Score؟",
        body: "DNA Risk Score هو تحليل جيني يقيس الاستعداد الوراثي للإصابة بأمراض محددة. يعتمد على تحليل آلاف المتغيرات الجينية (SNPs) المرتبطة بأمراض القلب، السكري، الأورام، وغيرها. النتيجة: درجة خطر شخصية لكل مرض.",
      },
      {
        icon: "ri-bar-chart-line",
        heading: "كيف نستخدم النتائج؟",
        body: "DNA Risk Score ليس حكماً نهائياً — الجينات تحدد الاستعداد، لكن البيئة ونمط الحياة يحددان ما إذا كان هذا الاستعداد سيتحول إلى مرض. نستخدم النتائج لتصميم خطة وقاية شخصية: فحوصات مبكرة، تعديلات غذائية، وتدخلات دوائية وقائية عند الحاجة.",
      },
      {
        icon: "ri-heart-pulse-line",
        heading: "مثال عملي: خطر أمراض القلب",
        body: "شخص بدرجة خطر جيني مرتفعة لأمراض القلب يحتاج: فحص كالسيوم الشرايين التاجية مبكراً، مراقبة دقيقة لمستوى LDL، وربما علاجاً وقائياً قبل ظهور أي أعراض. هذا التدخل المبكر قد يمنع نوبة قلبية بعد 20 سنة.",
      },
      {
        icon: "ri-shield-check-line",
        heading: "الخصوصية والأمان",
        body: "بيانات DNA حساسة للغاية. في ذا مديكال أفينيو، نلتزم بأعلى معايير حماية البيانات الجينية. لا تُشارك النتائج مع أي جهة خارجية، وتُحفظ بتشفير كامل. المريض يملك بياناته الجينية بالكامل.",
      },
    ],
    en: [
      {
        icon: "ri-dna-line",
        heading: "What is a DNA Risk Score?",
        body: "A DNA Risk Score is a genetic analysis that measures genetic predisposition to specific diseases. It depends on analyzing thousands of genetic variants (SNPs) associated with heart disease, diabetes, tumors, and others. The result: a personalized risk score for each disease.",
      },
      {
        icon: "ri-bar-chart-line",
        heading: "How Do We Use the Results?",
        body: "A DNA Risk Score is not a final judgment — genes determine predisposition, but environment and lifestyle determine whether this predisposition turns into a disease. We use the results to design a personalized prevention plan: early testing, dietary adjustments, and preventive drug interventions when needed.",
      },
      {
        icon: "ri-heart-pulse-line",
        heading: "Practical Example: Heart Disease Risk",
        body: "A person with a high genetic risk score for heart disease needs: coronary artery calcium scan early, close monitoring of LDL levels, and perhaps preventive treatment before any symptoms appear. This early intervention could prevent a heart attack 20 years later.",
      },
      {
        icon: "ri-shield-check-line",
        heading: "Privacy and Security",
        body: "DNA data is extremely sensitive. At The Medical Avenue, we are committed to the highest standards of genetic data protection. Results are not shared with any external parties and are saved with full encryption. The patient fully owns their genetic data.",
      },
    ]
  },
  "biological-aging": {
    ar: [
      {
        icon: "ri-time-line",
        heading: "العمر الزمني مقابل العمر البيولوجي",
        body: "شخصان في الخمسين من العمر قد يكون أحدهما بيولوجياً في الأربعين والآخر في الستين. العمر البيولوجي يقيس مدى تقدم الشيخوخة الخلوية الفعلية، وهو مؤشر أدق بكثير من العمر الزمني في التنبؤ بالصحة المستقبلية.",
      },
      {
        icon: "ri-test-tube-line",
        heading: "كيف نقيس العمر البيولوجي؟",
        body: "نستخدم عدة مؤشرات: ساعة الميثيلة (Epigenetic Clock)، طول التيلومير، مؤشرات الالتهاب المزمن (IL-6, TNF-α)، وظيفة الميتوكوندريا، ومؤشرات الإجهاد التأكسدي. مجموع هذه المؤشرات يعطي صورة دقيقة للعمر البيولوجي الفعلي.",
      },
      {
        icon: "ri-leaf-line",
        heading: "ما الذي يُسرّع الشيخوخة البيولوجية؟",
        body: "الإجهاد المزمن، قلة النوم، التغذية السيئة، الخمول البدني، والتعرض للملوثات البيئية كلها تُسرّع الشيخوخة الخلوية. الخبر الجيد: هذه العوامل قابلة للتعديل، والشيخوخة البيولوجية يمكن إبطاؤها بل وعكسها جزئياً.",
      },
      {
        icon: "ri-arrow-up-circle-line",
        heading: "خطة إبطاء الشيخوخة البيولوجية",
        body: "بناءً على نتائج التقييم، نصمم خطة شخصية تشمل: تحسين النوم، التغذية المضادة للالتهاب، التمارين المناسبة لعمرك البيولوجي، المكملات الغذائية المستهدفة، وإدارة الإجهاد. نُعيد القياس بعد 6 أشهر لنرى التحسن الفعلي.",
      },
    ],
    en: [
      {
        icon: "ri-time-line",
        heading: "Chronological vs. Biological Age",
        body: "Two people aged fifty may be biologically forty and sixty. Biological age measures the rate of actual cellular aging, which is a much more accurate predictor of future health than chronological age.",
      },
      {
        icon: "ri-test-tube-line",
        heading: "How Do We Measure Biological Age?",
        body: "We use several indicators: epigenetic clock (methylation), telomere length, chronic inflammation indicators (IL-6, TNF-alpha), mitochondrial function, and oxidative stress indicators. The sum of these indicators gives an accurate picture of actual biological age.",
      },
      {
        icon: "ri-leaf-line",
        heading: "What Accelerates Biological Aging?",
        body: "Chronic stress, lack of sleep, poor nutrition, physical inactivity, and exposure to environmental toxins all accelerate cellular aging. The good news: these factors are modifiable, and biological aging can be slowed and even partially reversed.",
      },
      {
        icon: "ri-arrow-up-circle-line",
        heading: "Plan to Slow Biological Aging",
        body: "Based on the assessment results, we design a personalized plan including: optimized sleep, anti-inflammatory nutrition, exercises suitable for your biological age, targeted nutritional supplements, and stress management. We re-measure after 6 months to see actual improvement.",
      },
    ]
  },
  "psychiatry-depth": {
    ar: [
      {
        icon: "ri-mental-health-line",
        heading: "الطب النفسي التقليدي وحدوده",
        body: "الطب النفسي التقليدي يعتمد على الأعراض الظاهرة لتحديد التشخيص والعلاج. لكن نفس الأعراض قد تنتج عن أسباب بيولوجية مختلفة تماماً: خلل في الناقلات العصبية، التهاب عصبي، خلل هرموني، أو استعداد جيني. علاج الأعراض دون فهم السبب يُعطي نتائج جزئية.",
      },
      {
        icon: "ri-stack-line",
        heading: "التقييم النفسي متعدد الطبقات",
        body: "في ذا مديكال أفينيو، التقييم النفسي يشمل: التاريخ النفسي والاجتماعي، الفحص العصبي النفسي، تحاليل الناقلات العصبية، الهرمونات (الغدة الدرقية، الكورتيزول)، مؤشرات الالتهاب، وأحياناً التحليل الجيني للاستجابة للأدوية (Pharmacogenomics).",
      },
      {
        icon: "ri-dna-line",
        heading: "Pharmacogenomics: الدواء المناسب لجيناتك",
        body: "لماذا يستجيب بعض المرضى لمضادات الاكتئاب ولا يستجيب آخرون؟ الجواب في الجينات. تحليل Pharmacogenomics يكشف كيف يُحلّل جسمك الأدوية النفسية، مما يُساعد في اختيار الدواء الأنسب والجرعة الصحيحة من البداية — بدلاً من التجربة والخطأ.",
      },
      {
        icon: "ri-user-heart-line",
        heading: "الصحة النفسية والجسدية: وجهان لعملة واحدة",
        body: "الاكتئاب يرفع خطر أمراض القلب. القلق المزمن يُسرّع الشيخوخة البيولوجية. الألم المزمن يُغير بنية الدماغ. في ذا مديكال أفينيو، نتعامل مع الصحة النفسية كجزء لا يتجزأ من الصحة العامة، لا كتخصص منفصل.",
      },
    ],
    en: [
      {
        icon: "ri-mental-health-line",
        heading: "Traditional Psychiatry & Its Limits",
        body: "Traditional psychiatry relies on visible symptoms to determine diagnosis and treatment. But the same symptoms may result from completely different biological causes: neurotransmitter imbalance, neuroinflammation, hormonal imbalance, or genetic predisposition. Treating symptoms without understanding the cause gives partial results.",
      },
      {
        icon: "ri-stack-line",
        heading: "Multi-layer Psychiatric Assessment",
        body: "At The Medical Avenue, psychiatric assessment includes: psychiatric and social history, neuropsychological examination, neurotransmitter analyses, hormones (thyroid, cortisol), inflammation indicators, and sometimes genetic analysis of drug response (Pharmacogenomics).",
      },
      {
        icon: "ri-dna-line",
        heading: "Pharmacogenomics: The Right Medication for Your Genes",
        body: "Why do some patients respond to antidepressants while others do not? The answer is in the genes. Pharmacogenomics analysis reveals how your body metabolizes psychiatric drugs, helping to choose the best drug and correct dose from the start — instead of trial and error.",
      },
      {
        icon: "ri-user-heart-line",
        heading: "Mental and Physical Health: Two Sides of the Same Coin",
        body: "Depression increases the risk of heart disease. Chronic anxiety accelerates biological aging. Chronic pain changes brain structure. At The Medical Avenue, we treat mental health as an integral part of general health, not as a separate specialty.",
      },
    ]
  },
};

export default function BlogDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const { posts: blogPosts, categories, loading: listLoading } = usePublicBlog();
  const [localPost, setLocalPost] = useState<any>(null);
  const [localLoading, setLocalLoading] = useState(false);

  const post = blogPosts.find((p) => p.id === id) || localPost;
  const related = blogPosts.filter((p) => p.id !== id).slice(0, 3);
  const isAr = !i18n.language?.startsWith("en");
  const sections = id ? (articleSections[id]?.[isAr ? "ar" : "en"] ?? []) : [];

  useEffect(() => {
    const fetchSinglePost = async () => {
      if (listLoading || !id) return;
      
      const foundInList = blogPosts.find((p) => p.id === id);
      if (foundInList) return;

      try {
        setLocalLoading(true);
        const { getPost, adaptPost } = await import("@/api/blog");
        const backendPost = await getPost(id);
        setLocalPost(adaptPost(backendPost, categories));
      } catch (err) {
        console.error("Post not found", err);
        navigate("/blog");
      } finally {
        setLocalLoading(false);
      }
    };

    fetchSinglePost();
  }, [id, listLoading, blogPosts, categories, navigate]);

  useEffect(() => {
    if (post) {
      window.scrollTo(0, 0);
    }
  }, [post]);

  const isLoading = listLoading || localLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-cream-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-brand-forest-200 border-t-brand-forest-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-brand-cream-50 font-sans">
      <Navbar />

      {/* ── Hero Image ────────────────────────────────────────────────────── */}
      <div className="relative pt-20" style={{ height: "460px" }}>
        <img
          src={post.image}
          alt={getLocalized(i18n.language, post.title, post.titleEn)}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-forest-950/85 via-brand-forest-900/40 to-transparent"></div>

        {/* Breadcrumb */}
        <div className="absolute top-24 right-0 left-0 max-w-7xl mx-auto px-4 md:px-8">
          <nav className="flex items-center gap-2 text-white/70 text-xs">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">{t("nav.home")}</Link>
            <i className={i18n.language === "ar" ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"}></i>
            <Link to="/blog" className="hover:text-white transition-colors cursor-pointer">{t("blog.hero.badge")}</Link>
            <i className={i18n.language === "ar" ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"}></i>
            <span className="text-white/90 font-medium line-clamp-1">{getLocalized(i18n.language, post.title, post.titleEn)}</span>
          </nav>
        </div>

        <div className="absolute bottom-0 right-0 left-0 p-8 max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-block bg-brand-forest-600 text-white text-xs font-bold px-3 py-1 rounded-full">
              {getLocalized(i18n.language, post.category, post.categoryEn)}
            </span>
            {post.featured && (
              <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                <i className="ri-star-fill text-[10px]"></i>
                {t("blog.featured_badge")}
              </span>
            )}
          </div>
          <h1 className="text-2xl md:text-4xl font-black text-white leading-tight">{getLocalized(i18n.language, post.title, post.titleEn)}</h1>
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* ── Article ── */}
          <article className="flex-1 min-w-0">

            {/* Meta Bar */}
            <div className="bg-white rounded-2xl border border-brand-cream-200 p-5 mb-8 flex flex-wrap items-center gap-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-brand-cream-100 flex-shrink-0">
                  <img src={post.authorImage} alt={getLocalized(i18n.language, post.author, post.authorEn)} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{getLocalized(i18n.language, post.author, post.authorEn)}</p>
                  <p className="text-brand-forest-600 text-xs font-medium">{getLocalized(i18n.language, post.authorRole, post.authorRoleEn)}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-gray-400 ms-auto">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-brand-forest-500"></i>
                  {post.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-brand-forest-500"></i>
                  {getLocalized(i18n.language, post.readTime, post.readTimeEn)} {t("blog.read")}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-brand-forest-500"></i>
                  {post.views.toLocaleString()} {t("blog.views")}
                </span>
              </div>
            </div>

            {/* Excerpt Highlight */}
            <div className={`bg-brand-cream-100 ${isAr ? "border-r-4" : "border-l-4"} border-brand-forest-500 rounded-xl p-5 mb-8`}>
              <p className="text-brand-forest-800 text-base leading-relaxed font-medium">{getLocalized(i18n.language, post.excerpt, post.excerptEn)}</p>
            </div>

            {/* Sections */}
            <div className="bg-white rounded-2xl border border-brand-cream-200 p-8 mb-8">
              {sections.length > 0 ? (
                <div className="space-y-8">
                  {sections.map((section, i) => (
                    <div key={i}>
                      <h2 className="text-lg font-black text-gray-900 mb-3 flex items-center gap-3">
                        {section.icon ? (
                          <div className="w-8 h-8 flex items-center justify-center bg-brand-cream-100 rounded-lg flex-shrink-0">
                            <i className={`${section.icon} text-brand-forest-600 text-base`}></i>
                          </div>
                        ) : (
                          <span className="w-6 h-6 flex items-center justify-center bg-brand-cream-200 text-brand-forest-700 text-xs font-black rounded-full flex-shrink-0">
                            {i + 1}
                          </span>
                        )}
                        {section.heading}
                      </h2>
                      <p className={`text-gray-600 leading-relaxed text-sm ${isAr ? "pr-11" : "pl-11"}`}>{section.body}</p>
                      {i < sections.length - 1 && (
                        <div className="mt-6 h-px bg-brand-cream-100"></div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div 
                  className="prose prose-brand max-w-none text-gray-600 leading-relaxed" 
                  dangerouslySetInnerHTML={{ __html: getLocalized(i18n.language, post.content, post.contentEn) }} 
                />
              )}
            </div>

            {/* Precision Medicine Note */}
            <div className="bg-brand-cream-50 rounded-2xl border border-brand-cream-300 p-5 mb-8 flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-brand-cream-200 rounded-xl flex-shrink-0">
                <i className="ri-dna-line text-brand-forest-600 text-lg"></i>
              </div>
              <div>
                <p className="font-bold text-brand-forest-800 text-sm mb-1">{t("blog.detail.personal_evaluation_prompt")}</p>
                <p className="text-brand-forest-700 text-xs leading-relaxed">
                  {t("blog.detail.personal_evaluation_desc")}
                </p>
                <Link
                  to="/booking"
                  className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold text-brand-forest-700 bg-brand-cream-200 hover:bg-brand-cream-300 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line"></i>
                  {t("dna.result.book_real")}
                </Link>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-brand-cream-100 text-brand-forest-700 text-xs font-medium px-3 py-1.5 rounded-full border border-brand-cream-200"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Share */}
            <div className="bg-white rounded-2xl border border-brand-cream-200 p-5 flex items-center justify-between flex-wrap gap-4">
              <p className="font-bold text-gray-800 text-sm">{t("blog.detail.share_article")}</p>
              <div className="flex gap-2">
                {[
                  { icon: "ri-twitter-x-line", label: "Twitter" },
                  { icon: "ri-whatsapp-line", label: "WhatsApp" },
                  { icon: "ri-linkedin-line", label: "LinkedIn" },
                ].map((s) => (
                  <a
                    key={s.label}
                    href="#"
                    title={s.label}
                    className="w-9 h-9 flex items-center justify-center bg-brand-cream-50 border border-brand-cream-200 hover:bg-brand-forest-600 hover:text-white hover:border-brand-forest-600 rounded-full transition-colors cursor-pointer text-gray-600"
                  >
                    <i className={`${s.icon} text-base`}></i>
                  </a>
                ))}
                <button
                  onClick={handleCopy}
                  className="w-9 h-9 flex items-center justify-center bg-brand-cream-50 border border-brand-cream-200 hover:bg-brand-forest-600 hover:text-white hover:border-brand-forest-600 rounded-full transition-colors cursor-pointer text-gray-600"
                  title={t("blog.detail.copy_link")}
                >
                  <i className={`${copied ? "ri-check-line" : "ri-link"} text-base`}></i>
                </button>
              </div>
            </div>

            {/* Author Card */}
            <div className="mt-8 bg-white rounded-2xl border border-brand-cream-200 p-6 flex items-start gap-5">
              <div className="w-16 h-16 rounded-2xl overflow-hidden bg-brand-cream-100 flex-shrink-0">
                <img src={post.authorImage} alt={getLocalized(i18n.language, post.author, post.authorEn)} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <p className="font-black text-gray-900 mb-0.5">{getLocalized(i18n.language, post.author, post.authorEn)}</p>
                <p className="text-brand-forest-600 text-xs font-semibold mb-2">{getLocalized(i18n.language, post.authorRole, post.authorRoleEn)}</p>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {t("blog.detail.author_bio")}
                </p>
                <Link
                  to="/doctors"
                  className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-brand-forest-600 hover:underline cursor-pointer"
                >
                  {t("blog.detail.view_full_profile")}
                  <i className={i18n.language === "ar" ? "ri-arrow-left-line" : "ri-arrow-right-line"}></i>
                </Link>
              </div>
            </div>
          </article>

          {/* ── Sidebar ── */}
          <aside className="lg:w-72 flex-shrink-0 space-y-6">

            {/* Book CTA */}
            <div className="bg-brand-forest-800 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-white/15 rounded-xl mx-auto mb-4">
                <i className="ri-calendar-check-line text-white text-2xl"></i>
              </div>
              <h3 className="font-black text-white mb-2">{t("dna.result.book_real")}</h3>
              <p className="text-brand-cream-300 text-xs mb-4 leading-relaxed">
                {t("blog.detail.sidebar_book_desc")}
              </p>
              <Link
                to="/booking"
                className="block w-full bg-brand-cream-300 text-brand-forest-900 font-bold py-2.5 rounded-xl text-sm hover:bg-brand-cream-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t("services.book")}
              </Link>
              <Link
                to="/packages"
                className="block w-full mt-2 border border-white/30 text-white font-medium py-2.5 rounded-xl text-xs hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t("blog.detail.view_care_levels")}
              </Link>
            </div>

            {/* Three Dimensions */}
            <div className="bg-white rounded-2xl border border-brand-cream-200 p-5">
              <h3 className="font-black text-gray-900 mb-4 text-sm flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center bg-brand-cream-100 rounded-lg">
                  <i className="ri-focus-3-line text-brand-forest-600 text-xs"></i>
                </div>
                {t("blog.detail.dimensions_title")}
              </h3>
              <div className="space-y-3">
                {[
                  { icon: "ri-stethoscope-line", title: t("blog.pillar1_title"), desc: t("blog.detail.clinical_depth_sub") },
                  { icon: "ri-dna-line", title: t("blog.pillar2_title"), desc: t("blog.detail.technical_depth_sub") },
                  { icon: "ri-global-line", title: t("blog.pillar3_title"), desc: t("blog.detail.international_depth_sub") },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-brand-cream-50 rounded-xl">
                    <div className="w-8 h-8 flex items-center justify-center bg-brand-cream-200 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-brand-forest-600 text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-800">{item.title}</p>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Posts */}
            <div className="bg-white rounded-2xl border border-brand-cream-200 p-5">
              <h3 className="font-black text-gray-900 mb-4 text-sm flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center bg-brand-cream-100 rounded-lg">
                  <i className="ri-article-line text-brand-forest-600 text-xs"></i>
                </div>
                {t("blog.related")}
              </h3>
              <div className="space-y-4">
                {related.map((rp) => (
                  <Link
                    key={rp.id}
                    to={`/blog/${rp.id}`}
                    className="flex gap-3 group cursor-pointer"
                  >
                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-brand-cream-100">
                      <img
                        src={rp.image}
                        alt={getLocalized(i18n.language, rp.title, rp.titleEn)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-800 leading-tight mb-1 group-hover:text-brand-forest-600 transition-colors line-clamp-2">
                        {getLocalized(i18n.language, rp.title, rp.titleEn)}
                      </p>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {getLocalized(i18n.language, rp.readTime, rp.readTimeEn)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <Link
                to="/blog"
                className="block w-full text-center mt-4 text-brand-forest-600 text-xs font-semibold hover:text-brand-forest-700 cursor-pointer"
              >
                {t("blog.detail.view_all_articles")}
                <i className={isAr ? "ri-arrow-left-line" : "ri-arrow-right-line"}></i>
              </Link>
            </div>

            {/* Services Quick Links */}
            <div className="bg-brand-cream-50 rounded-2xl border border-brand-cream-200 p-5">
              <h3 className="font-black text-gray-900 mb-3 text-sm">{t("blog.detail.related_services")}</h3>
              <div className="space-y-2">
                {[
                  { label: t("blog.detail.related_service_1"), href: "/services" },
                  { label: t("blog.detail.related_service_2"), href: "/services" },
                  { label: t("blog.detail.related_service_3"), href: "/services" },
                  { label: t("blog.detail.related_service_4"), href: "/packages" },
                ].map((link, i) => (
                  <Link
                    key={i}
                    to={link.href}
                    className="flex items-center justify-between text-xs text-gray-700 hover:text-brand-forest-600 transition-colors cursor-pointer py-1.5 border-b border-brand-cream-100 last:border-0"
                  >
                    <span>{link.label}</span>
                    <i className={isAr ? "ri-arrow-left-line" : "ri-arrow-right-line"}></i>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* ── More Articles ─────────────────────────────────────────────────── */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 bg-brand-forest-600 rounded-full"></div>
              <h2 className="text-xl font-black text-gray-900">{t("blog.detail.other_articles")}</h2>
            </div>
            <Link
              to="/blog"
              className="text-brand-forest-600 text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap"
            >
              {t("blog.detail.view_all")}
              <i className={isAr ? "ri-arrow-left-line" : "ri-arrow-right-line"}></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((rp) => (
              <Link
                key={rp.id}
                to={`/blog/${rp.id}`}
                className="group bg-brand-cream-50 rounded-2xl overflow-hidden border border-brand-cream-200 hover:border-brand-forest-300 transition-all duration-300 hover:-translate-y-1 cursor-pointer flex flex-col"
              >
                <div className="relative overflow-hidden" style={{ height: "180px" }}>
                  <img
                    src={rp.image}
                    alt={getLocalized(i18n.language, rp.title, rp.titleEn)}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 end-3">
                    <span className="bg-brand-forest-600/90 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                      {getLocalized(i18n.language, rp.category, rp.categoryEn)}
                    </span>
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-black text-gray-900 text-sm mb-2 leading-tight group-hover:text-brand-forest-700 transition-colors line-clamp-2">
                    {getLocalized(i18n.language, rp.title, rp.titleEn)}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed flex-1 line-clamp-2">{getLocalized(i18n.language, rp.excerpt, rp.excerptEn)}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-brand-cream-100">
                    <span className="text-xs text-gray-500">{getLocalized(i18n.language, rp.author, rp.authorEn)}</span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {getLocalized(i18n.language, rp.readTime, rp.readTimeEn)}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
